import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "contractType",
    "schoolStaff",
    "hourlyRateContainer",
    "hourlyRatesSection",
    "hourlyRateTemplate",
    "hourlyRatesContainer",
    "benefitCheckbox",
    "benefitInput",
    "chargeCheckbox",
    "chargeInput",
    "bankAccountContainer",
    "mobileMoneyContainer",
    "bankAccountTemplate",
    "mobileMoneyTemplate",
    "paymentMethodType"
  ];

  connect() {
    // Initialiser les données stockées
    this.currentClassrooms = [];
    this.currentSubjects = [];

    // Initialiser isSecondarySchool depuis l'attribut data (niveau école)
    this.isSecondarySchool = this.hasHourlyRatesSectionTarget &&
      this.hourlyRatesSectionTarget.dataset.isSecondarySchool === 'true';
    // The hourly rates follow the PERSON, not the contract type: the server
    // says whether the selected staff member teaches (`teaches`), first when
    // it renders the form, then each time the staff member changes.
    this.staffTeaches = this.hasHourlyRatesSectionTarget &&
      this.hourlyRatesSectionTarget.dataset.offered === 'true';

    this.toggleHourlyRate();
    this.initializeBenefitInputs();
    this.initializeChargeInputs();
    this.initializePaymentMethods();
    this.initializePayrollCalculator();
    
    // Ajouter un listener pour débugger avant soumission
    this.addFormSubmitListener();
    
    // Pour l'édition : charger les données si un enseignant est déjà sélectionné
    setTimeout(() => {
      this.triggerPayrollCalculation();
      
      // Si on est en mode édition et qu'un enseignant est sélectionné, charger ses cours
      if (this.hasSchoolStaffTarget && this.schoolStaffTarget.value && this.hourlyRatesOffered()) {
        // Vérifier si les dropdowns sont déjà remplis (mode édition)
        const existingDropdowns = this.hasHourlyRatesContainerTarget ? 
          this.hourlyRatesContainerTarget.querySelectorAll('select[name*="[classroom_id]"], select[name*="[subject_id]"]') : [];
        
        let hasPrefilledOptions = false;
        existingDropdowns.forEach(select => {
          if (select.options.length > 1) { // Plus que juste l'option placeholder
            hasPrefilledOptions = true;
          }
        });
        
        if (hasPrefilledOptions) {
          // Si déjà rempli, extraire les données pour les nouveaux taux horaires
          this.extractCurrentDataFromDropdowns();
        } else {
          // Si pas d'options pré-remplies, charger via AJAX
          this.updateHourlyRateDropdowns();
        }
      }
    }, 300);
  }

  // The card shows when the school has a secondary cycle and the selected
  // staff member teaches, whatever the contract type. Rates already entered
  // keep it open: they are paid, so they stay in sight.
  hourlyRatesOffered() {
    return this.hasExistingHourlyRatesWithData() || (this.isSecondarySchool && this.staffTeaches);
  }

  toggleHourlyRate() {
    if (!this.hasHourlyRatesSectionTarget) return;

    const shouldShow = this.hourlyRatesOffered();
    this.hourlyRatesSectionTarget.style.display = shouldShow ? '' : 'none';
    if (!shouldShow) this.hideNoCoursesMessage();
  }

  // Called when the selected staff member changes: the server says whether
  // they teach, and which classes and subjects their rates can bear on.
  async onTeacherChange() {
    if (!this.schoolStaffTarget.value) {
      this.currentClassrooms = [];
      this.currentSubjects = [];
      this.staffTeaches = false;
      this.toggleHourlyRate();
      return;
    }

    await this.updateHourlyRateDropdowns(true);
    this.toggleHourlyRate();
  }

  // Extraire les données des dropdowns existants (mode édition)
  extractCurrentDataFromDropdowns() {
    if (!this.hasHourlyRatesContainerTarget) return;

    const classroomSelects = this.hourlyRatesContainerTarget.querySelectorAll('select[name*="[classroom_id]"]');
    const subjectSelects = this.hourlyRatesContainerTarget.querySelectorAll('select[name*="[subject_id]"]');

    // Extraire les options des classes
    this.currentClassrooms = [];
    if (classroomSelects.length > 0) {
      const firstClassroomSelect = classroomSelects[0];
      Array.from(firstClassroomSelect.options).forEach(option => {
        if (option.value) { // Ignorer l'option placeholder
          this.currentClassrooms.push({
            id: parseInt(option.value),
            name: option.textContent
          });
        }
      });
    }

    // Extraire les options des matières
    this.currentSubjects = [];
    if (subjectSelects.length > 0) {
      const firstSubjectSelect = subjectSelects[0];
      Array.from(firstSubjectSelect.options).forEach(option => {
        if (option.value) { // Ignorer l'option placeholder
          this.currentSubjects.push({
            id: parseInt(option.value),
            name: option.textContent
          });
        }
      });
    }
  }

  // Méthode pour forcer la réinitialisation des selects existants
  reinitializeExistingSelects() {
    if (!this.hasHourlyRatesContainerTarget) return;

    const allSelects = this.hourlyRatesContainerTarget.querySelectorAll('select');

    allSelects.forEach((select) => {
      // Détruire l'instance Slim Select existante si elle existe
      if (select.slim) {
        try {
          select.slim.destroy();
          select.slim = null;
        } catch (error) {
          // Silently handle destruction errors
        }
      }

      // Réinitialiser avec Slim Select si disponible
      if (window.SlimSelect) {
        try {
          const newInstance = new window.SlimSelect({
            select: select
          });
          select.slim = newInstance;
        } catch (error) {
          // Fallback to standard HTML select if Slim Select fails
        }
      }
    });
  }

  // Met à jour les dropdowns des taux horaires basés sur l'enseignant sélectionné
  async updateHourlyRateDropdowns(onlyEmptySelects = false) {
    if (!this.hasSchoolStaffTarget || !this.schoolStaffTarget.value) {
      // Réinitialiser les données stockées
      this.currentClassrooms = [];
      this.currentSubjects = [];
      return;
    }

    const schoolStaffId = this.schoolStaffTarget.value;

    // CORRECTION : Ne réinitialiser les selects que si on ne préserve pas les données existantes
    if (!onlyEmptySelects) {
      this.reinitializeExistingSelects();
    }

    try {
      const response = await fetch(`/schools/payrolls/employee_profiles/teacher_courses_data?school_staff_id=${schoolStaffId}`, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      this.staffTeaches = data.teaches === true;
      this.toggleHourlyRate();

      // La visibilité est gérée au niveau de l'école, pas besoin de la mettre à jour ici
      // Si l'école n'est pas secondaire, pas besoin de peupler les dropdowns
      if (!this.isSecondarySchool) return;

      // Stocker les données pour les utiliser lors de l'ajout de nouveaux taux horaires
      this.currentClassrooms = data.classrooms;
      this.currentSubjects = data.subjects;

      // Vérifier si l'enseignant a des cours assignés
      // Ne pas afficher le message si des taux horaires existent déjà avec des données
      const hasExistingHourlyRates = this.hasExistingHourlyRatesWithData();

      if (data.classrooms.length === 0 && data.subjects.length === 0 && !hasExistingHourlyRates) {
        this.showNoCoursesMessage();
      } else {
        this.hideNoCoursesMessage();
        // CORRECTION : Utiliser le paramètre onlyEmptySelects
        this.updateAllHourlyRateSelects(data.classrooms, data.subjects, onlyEmptySelects);
      }

    } catch (error) {
      console.error('Error fetching teacher courses:', error);
      this.staffTeaches = false;
      // Réinitialiser en cas d'erreur
      this.currentClassrooms = [];
      this.currentSubjects = [];
    }
  }

  // Met à jour tous les selects de taux horaires avec les nouvelles données
  updateAllHourlyRateSelects(classrooms, subjects, onlyEmptySelects = false) {
    if (!this.hasHourlyRatesContainerTarget) return;

    const classroomSelects = this.hourlyRatesContainerTarget.querySelectorAll('select[name*="[classroom_id]"]');
    const subjectSelects = this.hourlyRatesContainerTarget.querySelectorAll('select[name*="[subject_id]"]');

    console.log(`Updating ${classroomSelects.length} classroom selects and ${subjectSelects.length} subject selects`);

    // Mettre à jour les selects de classes
    classroomSelects.forEach((select, index) => {
      const hasValue = select.value && select.value !== '';
      const hasOptions = select.options.length > 1;
      const needsPopulation = select.dataset.needsPopulation === 'classroom';
      
      console.log(`Classroom select ${index}: hasValue=${hasValue}, hasOptions=${hasOptions}, needsPopulation=${needsPopulation}`);
      
      // Si onlyEmptySelects est true, ne mettre à jour que les selects vraiment vides OU qui ont besoin de population
      if (onlyEmptySelects && hasValue && hasOptions && !needsPopulation) {
        console.log("Skipping classroom select with existing data:", select.value);
        return;
      }
      
      // Sauvegarder la valeur actuelle avant la mise à jour
      const currentValue = select.value;
      this.updateSelectOptions(select, classrooms, this.t('js.employee_profiles.select.classroom'));
      // Restaurer la valeur si elle existe toujours dans les nouvelles options
      if (currentValue && classrooms.find(c => c.id.toString() === currentValue)) {
        select.value = currentValue;
      }
      
      // Supprimer le marqueur de besoin de population
      if (needsPopulation) {
        delete select.dataset.needsPopulation;
      }
    });

    // Mettre à jour les selects de matières
    subjectSelects.forEach((select, index) => {
      const hasValue = select.value && select.value !== '';
      const hasOptions = select.options.length > 1;
      const needsPopulation = select.dataset.needsPopulation === 'subject';
      
      console.log(`Subject select ${index}: hasValue=${hasValue}, hasOptions=${hasOptions}, needsPopulation=${needsPopulation}`);
      
      // Si onlyEmptySelects est true, ne mettre à jour que les selects vraiment vides OU qui ont besoin de population
      if (onlyEmptySelects && hasValue && hasOptions && !needsPopulation) {
        console.log("Skipping subject select with existing data:", select.value);
        return;
      }
      
      // Sauvegarder la valeur actuelle avant la mise à jour
      const currentValue = select.value;
      this.updateSelectOptions(select, subjects, this.t('js.employee_profiles.select.subject'));
      // Restaurer la valeur si elle existe toujours dans les nouvelles options
      if (currentValue && subjects.find(s => s.id.toString() === currentValue)) {
        select.value = currentValue;
      }
      
      // Supprimer le marqueur de besoin de population
      if (needsPopulation) {
        delete select.dataset.needsPopulation;
      }
    });
  }

  // Afficher un message quand l'enseignant n'a pas de cours assignés
  showNoCoursesMessage() {
    if (!this.hasHourlyRatesSectionTarget) return;
    
    // Supprimer le message existant s'il y en a un
    this.hideNoCoursesMessage();
    
    // Créer le message d'information
    const messageDiv = document.createElement('div');
    messageDiv.className = 'bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-4';
    messageDiv.setAttribute('data-no-courses-message', '');
    messageDiv.innerHTML = `
      <div class="flex items-start">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
          </svg>
        </div>
        <div class="ml-3">
          <h3 class="text-sm font-medium text-yellow-800">
            ${this.t('js.employee_profiles.no_courses.title')}
          </h3>
          <div class="mt-2 text-sm text-yellow-700">
            <p>${this.t('js.employee_profiles.no_courses.description')}</p>
            <p class="mt-1">${this.t('js.employee_profiles.no_courses.help')}</p>
          </div>
        </div>
      </div>
    `;
    
    // Insérer le message au début de la section des taux horaires
    const addButton = this.hourlyRatesSectionTarget.querySelector('button[data-action*="addHourlyRate"]');
    if (addButton) {
      addButton.parentNode.insertBefore(messageDiv, addButton);
      // Désactiver le bouton d'ajout
      addButton.disabled = true;
      addButton.classList.add('opacity-50', 'cursor-not-allowed');
    }
  }

  // Masquer le message d'information
  hideNoCoursesMessage() {
    if (!this.hasHourlyRatesSectionTarget) return;
    
    const existingMessage = this.hourlyRatesSectionTarget.querySelector('[data-no-courses-message]');
    if (existingMessage) {
      existingMessage.remove();
    }
    
    // Réactiver le bouton d'ajout
    const addButton = this.hourlyRatesSectionTarget.querySelector('button[data-action*="addHourlyRate"]');
    if (addButton) {
      addButton.disabled = false;
      addButton.classList.remove('opacity-50', 'cursor-not-allowed');
    }
  }

  // Vérifier s'il existe des taux horaires avec des données valides
  hasExistingHourlyRatesWithData() {
    if (!this.hasHourlyRatesContainerTarget) return false;
    
    const hourlyRateItems = this.hourlyRatesContainerTarget.querySelectorAll('[data-hourly-rate-item]');
    
    for (const item of hourlyRateItems) {
      // Ignorer les éléments marqués pour suppression
      const destroyInput = item.querySelector('input[name*="[_destroy]"]');
      if (destroyInput && destroyInput.value === '1') continue;
      
      // Vérifier si l'élément est visible
      if (item.style.display === 'none') continue;
      
      // Vérifier si les selects ont des valeurs sélectionnées
      const classroomSelect = item.querySelector('select[name*="[classroom_id]"]');
      const subjectSelect = item.querySelector('select[name*="[subject_id]"]');
      
      const hasClassroomValue = classroomSelect && classroomSelect.value && classroomSelect.value !== '';
      const hasSubjectValue = subjectSelect && subjectSelect.value && subjectSelect.value !== '';
      
      // S'il y a au moins un taux horaire avec classroom ET subject, retourner true
      if (hasClassroomValue && hasSubjectValue) {
        return true;
      }
    }
    
    return false;
  }

  // Helper pour mettre à jour les options d'un select
  updateSelectOptions(select, options, placeholder) {
    try {
      console.log(`=== UPDATE SELECT OPTIONS ===`);
      console.log(`Select:`, select);
      console.log(`Options count:`, options.length);
      console.log(`Placeholder:`, placeholder);
      console.log(`Current value:`, select.value);
      
      const currentValue = select.value;
      
      // Vérifier si c'est un Slim Select
      let slimSelectInstance = select.slim;
      
      if (slimSelectInstance) {
        console.log('Using Slim Select');
        // Préparer les données pour Slim Select
        const slimOptions = [
          { text: placeholder, value: '' },
          ...options.map(option => ({
            text: option.name,
            value: option.id.toString()
          }))
        ];
        
        console.log('Slim options:', slimOptions);
        
        // Mettre à jour les options via l'API Slim Select
        slimSelectInstance.setData(slimOptions);
        
        // Restaurer la valeur sélectionnée si elle existe
        if (currentValue && options.find(opt => opt.id.toString() === currentValue)) {
          slimSelectInstance.set(currentValue);
          console.log('Restored value:', currentValue);
        }
        
      } else {
        console.log('Using standard HTML select');
        // Fallback pour les selects HTML normaux
        select.innerHTML = `<option value="">${placeholder}</option>`;
        
        options.forEach(option => {
          const optionElement = document.createElement('option');
          optionElement.value = option.id;
          optionElement.textContent = option.name;
          if (option.id.toString() === currentValue) {
            optionElement.selected = true;
          }
          select.appendChild(optionElement);
        });
        
        console.log(`Updated select with ${options.length} options. Final options count: ${select.options.length}`);
      }
      
    } catch (error) {
      console.error("Error in updateSelectOptions:", error);
      console.error("Select:", select);
      console.error("Options:", options);
    }
  }

  toggleBenefitInput(event) {
    const checkbox = event.target;
    const benefit = checkbox.dataset.benefit;
    const inputContainer = this.benefitInputTargets.find(input => input.dataset.benefit === benefit);

    if (inputContainer) {
      inputContainer.style.display = checkbox.checked ? '' : 'none';
      if (!checkbox.checked) {
        const input = inputContainer.querySelector('input[type="number"]');
        if (input) input.value = '';
      }
    }
    
    // Déclencher le recalcul des charges sociales après un court délai pour laisser le temps au DOM de se mettre à jour
    setTimeout(() => {
      this.triggerPayrollCalculation();
    }, 100);
  }

  toggleChargeInput(event) {
    const checkbox = event.target;
    const charge = checkbox.dataset.charge;
    const inputContainer = this.chargeInputTargets.find(input => input.dataset.charge === charge);

    if (inputContainer) {
      inputContainer.style.display = checkbox.checked ? '' : 'none';
      if (!checkbox.checked) {
        const input = inputContainer.querySelector('input[type="number"]');
        if (input) input.value = '';
      }
    }
  }

  // Nouvelle méthode pour initialiser le calculateur de paie
  initializePayrollCalculator() {
    // Les data-action dans le HTML gèrent maintenant les événements
    // Cette méthode peut être utilisée pour d'autres initialisations si nécessaire
  }

  // Méthode pour déclencher le recalcul des charges sociales
  triggerPayrollCalculation() {
    const payrollCalculator = this.application.getControllerForElementAndIdentifier(
      document.querySelector('[data-controller~="payroll-calculator"]'),
      'payroll-calculator'
    );
    
    if (payrollCalculator) {
      payrollCalculator.calculateAll();
    }
  }

  initializeBenefitInputs() {
    this.benefitCheckboxTargets.forEach(checkbox => {
      const benefit = checkbox.dataset.benefit;
      const inputContainer = this.benefitInputTargets.find(input => input.dataset.benefit === benefit);
      if (inputContainer) {
        inputContainer.style.display = checkbox.checked ? '' : 'none';
      }
    });
  }

  initializeChargeInputs() {
    this.chargeCheckboxTargets.forEach(checkbox => {
      const charge = checkbox.dataset.charge;
      const inputContainer = this.chargeInputTargets.find(input => input.dataset.charge === charge);
      if (inputContainer) {
        inputContainer.style.display = checkbox.checked ? '' : 'none';
      }
    });
  }

  addHourlyRate(event) {
    event.preventDefault();
    
    console.log('=== ADD HOURLY RATE ===');
    console.log('Template target exists:', this.hasHourlyRateTemplateTarget);
    console.log('Container target exists:', this.hasHourlyRatesContainerTarget);
    
    if (!this.hasHourlyRateTemplateTarget || !this.hasHourlyRatesContainerTarget) {
      console.error("Hourly rate template or container not found");
      return;
    }

    // Vérifier qu'un enseignant est sélectionné
    if (!this.hasSchoolStaffTarget || !this.schoolStaffTarget.value) {
      console.log('No school staff selected');
      alert(this.t('js.employee_profiles.errors.no_teacher_selected'));
      return;
    }
    
    console.log('School staff selected:', this.schoolStaffTarget.value);
    console.log('Current classrooms:', this.currentClassrooms);
    console.log('Current subjects:', this.currentSubjects);
    
    // Vérifier s'il y a un message "aucun cours" affiché
    if (this.hourlyRatesSectionTarget.querySelector('[data-no-courses-message]')) {
      console.log('No courses message displayed');
      alert(this.t('js.employee_profiles.errors.no_courses_assigned'));
      return;
    }
    
    const template = this.hourlyRateTemplateTarget.innerHTML;
    const timestamp = new Date().getTime();
    const newHtml = template.replace(/NEW_RECORD/g, timestamp);
    
    console.log('Adding new hourly rate with timestamp:', timestamp);
    console.log('Template HTML:', template.substring(0, 200) + '...');
    
    this.hourlyRatesContainerTarget.insertAdjacentHTML('beforeend', newHtml);
    
    console.log('New hourly rate added to DOM');
    
    // CORRECTION : Mettre à jour SEULEMENT les nouveaux selects vides
    setTimeout(() => {
      console.log('Updating selects after timeout...');
      if (this.currentClassrooms && this.currentSubjects) {
        console.log('Using cached data');
        // Utiliser onlyEmptySelects = true pour ne pas écraser les données existantes
        this.updateAllHourlyRateSelects(this.currentClassrooms, this.currentSubjects, true);
      } else {
        console.log('Fetching fresh data');
        // Si pas de données stockées, les récupérer
        this.updateHourlyRateDropdowns();
      }
    }, 100); // Réduire le timeout pour éviter les conditions de course
  }

  removeHourlyRate(event) {
    event.preventDefault();
    
    const hourlyRateItem = event.target.closest('[data-hourly-rate-item]');
    const destroyField = hourlyRateItem.querySelector('input[name*="[_destroy]"]');
    
    if (destroyField) {
      // Marquer pour suppression si c'est un enregistrement existant
      destroyField.value = '1';
      hourlyRateItem.style.display = 'none';
    } else {
      // Supprimer directement si c'est un nouvel enregistrement
      hourlyRateItem.remove();
    }
  }

  addBankAccount(event) {
    event.preventDefault();
    
    console.log('=== ADD BANK ACCOUNT ===');
    console.log('Template target exists:', this.hasBankAccountTemplateTarget);
    console.log('Container target exists:', this.hasBankAccountContainerTarget);
    
    if (!this.hasBankAccountTemplateTarget || !this.hasBankAccountContainerTarget) {
      console.error('Bank account template or container not found');
      return;
    }
    
    const template = this.bankAccountTemplateTarget.innerHTML;
    const timestamp = new Date().getTime();
    const newHtml = template.replace(/NEW_RECORD/g, timestamp);
    
    console.log('Adding bank account with timestamp:', timestamp);
    this.bankAccountContainerTarget.insertAdjacentHTML('beforeend', newHtml);
    
    // Réinitialiser la méthode de paiement par défaut
    const form = this.element.closest('form');
    form.querySelectorAll('input[type="radio"][name="employee_profile[default_payment_method]"]').forEach(r => {
      r.checked = false;
    });
    
    // Réinitialiser les champs cachés
    const typeInput = form.querySelector('input[name="employee_profile[default_payment_method_type]"]');
    const idInput = form.querySelector('input[name="employee_profile[default_payment_method_id]"]');
    if (typeInput && idInput) {
      typeInput.value = '';
      idInput.value = '';
    }
  }

  addMobileMoney(event) {
    event.preventDefault();
    
    console.log('=== ADD MOBILE MONEY ===');
    console.log('Template target exists:', this.hasMobileMoneyTemplateTarget);
    console.log('Container target exists:', this.hasMobileMoneyContainerTarget);
    
    if (!this.hasMobileMoneyTemplateTarget || !this.hasMobileMoneyContainerTarget) {
      console.error('Mobile money template or container not found');
      return;
    }
    
    const template = this.mobileMoneyTemplateTarget.innerHTML;
    const timestamp = new Date().getTime();
    const newHtml = template.replace(/NEW_RECORD/g, timestamp);
    
    console.log('Adding mobile money with timestamp:', timestamp);
    this.mobileMoneyContainerTarget.insertAdjacentHTML('beforeend', newHtml);
    
    // Réinitialiser la méthode de paiement par défaut
    const form = this.element.closest('form');
    form.querySelectorAll('input[type="radio"][name="employee_profile[default_payment_method]"]').forEach(r => {
      r.checked = false;
    });
    
    // Réinitialiser les champs cachés
    const typeInput = form.querySelector('input[name="employee_profile[default_payment_method_type]"]');
    const idInput = form.querySelector('input[name="employee_profile[default_payment_method_id]"]');
    if (typeInput && idInput) {
      typeInput.value = '';
      idInput.value = '';
    }
  }

  removePaymentMethod(event) {
    event.preventDefault();
    const paymentMethodDiv = event.target.closest('[data-payment-method]');
    const destroyField = paymentMethodDiv.querySelector('input[name*="[_destroy]"]');
    const radio = paymentMethodDiv.querySelector('input[type="radio"]');
    
    if (destroyField) {
      destroyField.value = '1';
      paymentMethodDiv.style.display = 'none';
      
      // Si c'était la méthode de paiement par défaut, réinitialiser les champs cachés
      if (radio && radio.checked) {
        const form = this.element.closest('form');
        form.querySelector('input[name="employee_profile[default_payment_method_type]"]').value = '';
        form.querySelector('input[name="employee_profile[default_payment_method_id]"]').value = '';
      }
    } else {
      paymentMethodDiv.remove();
    }
  }

  setDefaultPaymentMethod(event) {
    const radio = event.target;
    
    console.log('=== SET DEFAULT PAYMENT METHOD ===');
    console.log('Radio checked:', radio.checked);
    console.log('Type:', radio.dataset.type);
    console.log('ID:', radio.dataset.id);
    
    if (radio.checked) {
      const type = radio.dataset.type;
      const id = radio.dataset.id || 'new';
      
      // Décocher tous les autres boutons radio
      const form = this.element.closest('form');
      form.querySelectorAll('input[type="radio"][name="employee_profile[default_payment_method]"]').forEach(r => {
        if (r !== radio) r.checked = false;
      });
      
      // Mettre à jour les champs cachés
      const typeInput = form.querySelector('input[name="employee_profile[default_payment_method_type]"]');
      const idInput = form.querySelector('input[name="employee_profile[default_payment_method_id]"]');
      
      if (typeInput && idInput) {
        typeInput.value = type;
        idInput.value = id;

        // Vérifier si c'est une nouvelle méthode de paiement
        if (id === 'new') {
          // Trouver le conteneur de la méthode de paiement sélectionnée
          const paymentMethodDiv = radio.closest('[data-payment-method]');
          if (paymentMethodDiv) {
            // Vérifier que les champs requis sont remplis
            let isValid = true;
            paymentMethodDiv.querySelectorAll('input[required]').forEach(input => {
              if (!input.value) isValid = false;
            });
            
            if (!isValid) {
              radio.checked = false;
              typeInput.value = '';
              idInput.value = '';
            }
          }
        }
      }
    }
  }

  initializePaymentMethods() {
    const form = this.element.closest('form');
    const typeInput = form.querySelector('input[name="employee_profile[default_payment_method_type]"]');
    const idInput = form.querySelector('input[name="employee_profile[default_payment_method_id]"]');
    
    if (typeInput && idInput) {
      const type = typeInput.value;
      const id = idInput.value;
      
      if (type && id) {
        const radio = form.querySelector(`input[type="radio"][data-type="${type}"][data-id="${id}"]`);
        if (radio) {
          radio.checked = true;
        }
      }
    }
  }

  // Méthode de debugging pour vérifier les données avant soumission
  addFormSubmitListener() {
    const form = this.element.closest('form');
    if (form) {
      form.addEventListener('submit', (event) => {
        console.log('=== FORM SUBMISSION DEBUG ===');
        
        // Vérifier les taux horaires
        if (this.hasHourlyRatesContainerTarget) {
          const hourlyRateInputs = this.hourlyRatesContainerTarget.querySelectorAll('[data-hourly-rate-item]');
          console.log(`Found ${hourlyRateInputs.length} hourly rate items`);
          
          hourlyRateInputs.forEach((item, index) => {
            const classroomSelect = item.querySelector('select[name*="[classroom_id]"]');
            const subjectSelect = item.querySelector('select[name*="[subject_id]"]');
            const rateInput = item.querySelector('input[name*="[rate_amount]"]');
            const destroyInput = item.querySelector('input[name*="[_destroy]"]');
            
            console.log(`Hourly rate ${index + 1}:`);
            console.log(`  - classroom_id: ${classroomSelect ? classroomSelect.value : 'NOT FOUND'}`);
            console.log(`  - subject_id: ${subjectSelect ? subjectSelect.value : 'NOT FOUND'}`);
            console.log(`  - rate_amount: ${rateInput ? rateInput.value : 'NOT FOUND'}`);
            console.log(`  - _destroy: ${destroyInput ? destroyInput.value : 'NOT FOUND'}`);
            console.log(`  - classroom options: ${classroomSelect ? classroomSelect.options.length : 0}`);
            console.log(`  - subject options: ${subjectSelect ? subjectSelect.options.length : 0}`);
          });
        }
        
        // Ne pas empêcher la soumission, juste logger
      });
    }
  }

  t(key, options = {}) {
    if (typeof window !== "undefined" && typeof window.t === "function") {
      return window.t(key, options);
    }
    return key;
  }
}
